import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Progress } from './ui/progress';
import { RadioGroup, RadioGroupItem } from './ui/radio-group';
import { useToast } from '../hooks/use-toast';
import {
  Plus, FileText, Calendar, Clock, MapPin, ClipboardCheck, CheckCircle2, 
  AlertTriangle, Shield, Sparkles, RefreshCw, Building, TrendingUp,
  Truck, ArrowUpFromLine, Zap, Flame, Box, Lightbulb, HardHat, Siren,
  User, ChevronRight, ChevronLeft, Download, Eye, Edit, Trash2,
  Play, Pause, BarChart3, Target, AlertCircle, CheckCircle, XCircle,
  FileDown, Loader2, ArrowRight, List, Grid3X3, Search, Filter
} from 'lucide-react';
import PageHeader from './ui/PageHeader';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Category icon mapping
const categoryIcons = {
  forklift: Truck,
  lifts: ArrowUpFromLine,
  ladders: List,
  electrical: Zap,
  confined_space: Box,
  lighting: Lightbulb,
  ppe: HardHat,
  fire_safety: Flame,
  housekeeping: Sparkles,
  emergency: Siren,
  hazcom: AlertTriangle,
  ergonomics: User,
};

const getCategoryIcon = (category) => categoryIcons[category] || ClipboardCheck;

// Status colors
const statusColors = {
  draft: 'bg-gray-100 text-gray-700 border-gray-300',
  in_progress: 'bg-blue-100 text-blue-700 border-blue-300',
  completed: 'bg-green-100 text-green-700 border-green-300',
  approved: 'bg-purple-100 text-purple-700 border-purple-300',
};

const complianceColors = {
  compliant: 'bg-green-100 text-green-700',
  non_compliant: 'bg-red-100 text-red-700',
  partial: 'bg-yellow-100 text-yellow-700',
  not_assessed: 'bg-gray-100 text-gray-600',
  not_applicable: 'bg-slate-100 text-slate-500',
};

const AuditsPage = () => {
  const [audits, setAudits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // list, conduct
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const { toast } = useToast();
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const headers = getAuthHeaders();
      
      const [auditsRes, companiesRes, templatesRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/api/comprehensive-audits`, { headers }),
        axios.get(`${API_URL}/api/companies`, { headers }),
        axios.get(`${API_URL}/api/comprehensive-audits/templates`, { headers }),
        axios.get(`${API_URL}/api/comprehensive-audits/dashboard/stats`, { headers }),
      ]);
      
      setAudits(auditsRes.data.audits || []);
      setCompanies(companiesRes.data || []);
      setTemplates(templatesRes.data.templates || []);
      setDashboardStats(statsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: "Error",
        description: "Failed to load audit data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = audit.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         audit.company_name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleViewAudit = async (auditId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.get(`${API_URL}/api/comprehensive-audits/${auditId}`, { headers });
      setSelectedAudit(response.data);
      setViewMode('conduct');
    } catch (error) {
      toast({ title: "Error", description: "Failed to load audit", variant: "destructive" });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {viewMode === 'list' && (
          <>
            {/* Unified Page Header */}
            <PageHeader
              title="Comprehensive Safety Audits"
              subtitle="Step-by-step compliance auditing across all safety categories"
              icon={ClipboardCheck}
              variant="blue"
              onRefresh={fetchData}
              loading={loading}
              actions={
                <Button onClick={() => setShowCreateDialog(true)} size="sm" className="bg-white/20 hover:bg-white/30 text-white border-0">
                  <Plus className="w-4 h-4 mr-2" />
                  New Audit
                </Button>
              }
              stats={dashboardStats ? [
                { icon: FileText, label: 'Total Audits', value: dashboardStats.total_audits, color: 'blue' },
                { icon: CheckCircle, label: 'Completed', value: dashboardStats.completed, color: 'green' },
                { icon: Clock, label: 'In Progress', value: dashboardStats.in_progress, color: 'yellow' },
                { icon: Target, label: 'Avg Score', value: `${dashboardStats.average_score}%`, color: 'purple' }
              ] : undefined}
            />

            {/* Category Health Overview */}
            {dashboardStats?.category_health?.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-blue-600" />
                    Category Compliance Health
                  </CardTitle>
                  <CardDescription>Areas that need attention (lowest scores first)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {dashboardStats.category_health.slice(0, 6).map((cat) => {
                      const Icon = getCategoryIcon(cat.category);
                      const scoreColor = cat.average_score >= 80 ? 'text-green-600' : 
                                        cat.average_score >= 60 ? 'text-yellow-600' : 'text-red-600';
                      return (
                        <div key={cat.category} className="p-3 rounded-lg border bg-white hover:shadow-md transition-shadow">
                          <div className="flex items-center gap-2 mb-2">
                            <Icon className="w-4 h-4 text-slate-600" />
                            <span className="text-xs font-medium truncate">{cat.name}</span>
                          </div>
                          <p className={`text-xl font-bold ${scoreColor}`}>{cat.average_score}%</p>
                          <p className="text-xs text-slate-500">{cat.audit_count} audits</p>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Search and Filters */}
            <Card>
              <CardContent className="py-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Search audits by name or company..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-full md:w-48">
                      <Filter className="w-4 h-4 mr-2" />
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Audits List */}
            <div className="space-y-4">
              {filteredAudits.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ClipboardCheck className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">No Audits Found</h3>
                    <p className="text-slate-600 mb-4">Create your first comprehensive safety audit to get started.</p>
                    <Button onClick={() => setShowCreateDialog(true)}>
                      <Plus className="w-4 h-4 mr-2" />
                      Create New Audit
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                filteredAudits.map((audit) => (
                  <AuditCard 
                    key={audit.id} 
                    audit={audit} 
                    onView={() => handleViewAudit(audit.id)}
                    onRefresh={fetchData}
                  />
                ))
              )}
            </div>
          </>
        )}

        {viewMode === 'conduct' && selectedAudit && (
          <AuditConductView
            audit={selectedAudit}
            onBack={() => { setViewMode('list'); setSelectedAudit(null); fetchData(); }}
            onUpdate={(updated) => setSelectedAudit(updated)}
          />
        )}

        {/* Create Audit Dialog */}
        <CreateAuditDialog
          open={showCreateDialog}
          onClose={() => setShowCreateDialog(false)}
          companies={companies}
          templates={templates}
          onSuccess={(auditId) => {
            setShowCreateDialog(false);
            handleViewAudit(auditId);
          }}
        />
      </div>
    </div>
  );
};

// Audit Card Component
const AuditCard = ({ audit, onView, onRefresh }) => {
  const scoreColor = audit.overall_score >= 80 ? 'text-green-600' : 
                    audit.overall_score >= 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4 md:p-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <h3 className="text-lg font-semibold text-slate-900">{audit.name}</h3>
              <Badge className={statusColors[audit.status]}>
                {audit.status?.replace('_', ' ')}
              </Badge>
            </div>
            <div className="flex flex-wrap gap-4 text-sm text-slate-600">
              <span className="flex items-center gap-1">
                <Building className="w-4 h-4" />
                {audit.company_name || 'N/A'}
              </span>
              {audit.site_location && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {audit.site_location}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(audit.created_at).toLocaleDateString()}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            {audit.status !== 'draft' && (
              <div className="text-center">
                <p className={`text-2xl font-bold ${scoreColor}`}>{audit.overall_score || 0}%</p>
                <p className="text-xs text-slate-500">Score</p>
              </div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={onView}>
                {audit.status === 'draft' ? (
                  <><Play className="w-4 h-4 mr-1" /> Start</>
                ) : audit.status === 'in_progress' ? (
                  <><Edit className="w-4 h-4 mr-1" /> Continue</>
                ) : (
                  <><Eye className="w-4 h-4 mr-1" /> View</>
                )}
              </Button>
            </div>
          </div>
        </div>
        
        {audit.status !== 'draft' && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-600 mb-1">
              <span>Progress</span>
              <span>{audit.compliant_items || 0}/{audit.total_items || 0} items assessed</span>
            </div>
            <Progress 
              value={((audit.compliant_items + audit.non_compliant_items) / (audit.total_items || 1)) * 100} 
              className="h-2"
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Create Audit Dialog
const CreateAuditDialog = ({ open, onClose, companies, templates, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    company_id: '',
    name: '',
    description: '',
    site_location: '',
    audit_type: 'comprehensive',
    selected_categories: []
  });
  const [creating, setCreating] = useState(false);
  const { toast } = useToast();

  const handleCreate = async () => {
    if (!formData.company_id || !formData.name) {
      toast({ title: "Error", description: "Please fill in required fields", variant: "destructive" });
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('auth_token');
      const payload = {
        ...formData,
        selected_categories: formData.audit_type === 'comprehensive' ? null : formData.selected_categories
      };
      
      const response = await axios.post(`${API_URL}/api/comprehensive-audits`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({ title: "Success", description: "Audit created successfully!" });
      onSuccess(response.data.id);
      setStep(1);
      setFormData({ company_id: '', name: '', description: '', site_location: '', audit_type: 'comprehensive', selected_categories: [] });
    } catch (error) {
      toast({ title: "Error", description: "Failed to create audit", variant: "destructive" });
    } finally {
      setCreating(false);
    }
  };

  const toggleCategory = (cat) => {
    setFormData(prev => ({
      ...prev,
      selected_categories: prev.selected_categories.includes(cat)
        ? prev.selected_categories.filter(c => c !== cat)
        : [...prev.selected_categories, cat]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create New Safety Audit</DialogTitle>
          <DialogDescription>
            {step === 1 ? 'Enter audit details' : 'Select categories to audit'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Company *</Label>
              <Select value={formData.company_id} onValueChange={(v) => setFormData({...formData, company_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Select company" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Audit Name *</Label>
              <Input 
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="e.g., Q4 2024 Annual Safety Audit"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Site Location</Label>
              <Input 
                value={formData.site_location}
                onChange={(e) => setFormData({...formData, site_location: e.target.value})}
                placeholder="e.g., Building A - Warehouse"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea 
                value={formData.description}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                placeholder="Optional notes about this audit..."
                rows={3}
              />
            </div>
            
            <div className="space-y-3">
              <Label>Audit Type</Label>
              <RadioGroup value={formData.audit_type} onValueChange={(v) => setFormData({...formData, audit_type: v})}>
                <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-slate-50">
                  <RadioGroupItem value="comprehensive" id="comprehensive" />
                  <Label htmlFor="comprehensive" className="flex-1 cursor-pointer">
                    <span className="font-medium">Comprehensive Audit</span>
                    <span className="block text-sm text-slate-500">All 12 safety categories ({templates.reduce((a, t) => a + t.item_count, 0)} checkpoints)</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 p-3 rounded-lg border hover:bg-slate-50">
                  <RadioGroupItem value="focused" id="focused" />
                  <Label htmlFor="focused" className="flex-1 cursor-pointer">
                    <span className="font-medium">Focused Audit</span>
                    <span className="block text-sm text-slate-500">Select specific categories to audit</span>
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            <p className="text-sm text-slate-600">Select the categories you want to include in this audit:</p>
            <div className="grid grid-cols-2 gap-3">
              {templates.map((template) => {
                const Icon = getCategoryIcon(template.category);
                const isSelected = formData.selected_categories.includes(template.category);
                return (
                  <button
                    key={template.category}
                    onClick={() => toggleCategory(template.category)}
                    className={`p-4 rounded-lg border-2 text-left transition-all ${
                      isSelected ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-slate-600'}`} />
                      <span className="font-medium text-sm">{template.name}</span>
                    </div>
                    <p className="text-xs text-slate-500">{template.item_count} checkpoints</p>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 2 && (
            <Button variant="outline" onClick={() => setStep(1)}>
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>
          )}
          {step === 1 && formData.audit_type === 'focused' ? (
            <Button onClick={() => setStep(2)}>
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleCreate} disabled={creating}>
              {creating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : 'Create Audit'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Audit Conduct View - Wizard & Tabbed Interface
const AuditConductView = ({ audit, onBack, onUpdate }) => {
  const [viewStyle, setViewStyle] = useState('wizard'); // wizard or tabbed
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState(0);
  const [localAudit, setLocalAudit] = useState(audit);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const { toast } = useToast();

  const categories = localAudit.categories || [];
  const currentCategory = categories[currentCategoryIndex];

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  };

  const handleStartAudit = async () => {
    try {
      await axios.post(`${API_URL}/api/comprehensive-audits/${localAudit.id}/start`, {}, {
        headers: getAuthHeaders()
      });
      setLocalAudit(prev => ({ ...prev, status: 'in_progress' }));
      toast({ title: "Audit Started", description: "You can now begin the inspection" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to start audit", variant: "destructive" });
    }
  };

  const handleItemUpdate = async (categoryIndex, itemIndex, updates) => {
    const newCategories = [...localAudit.categories];
    const item = newCategories[categoryIndex].items[itemIndex];
    Object.assign(item, updates);
    
    // Recalculate category stats
    const items = newCategories[categoryIndex].items;
    const total = items.length;
    const compliant = items.filter(i => i.status === 'compliant').length;
    const nonCompliant = items.filter(i => ['non_compliant', 'partial'].includes(i.status)).length;
    const notAssessed = items.filter(i => i.status === 'not_assessed').length;
    const assessed = total - notAssessed - items.filter(i => i.status === 'not_applicable').length;
    const score = assessed > 0 ? (compliant / assessed * 100) : 0;
    
    newCategories[categoryIndex] = {
      ...newCategories[categoryIndex],
      score: Math.round(score * 10) / 10,
      compliant_items: compliant,
      non_compliant_items: nonCompliant,
      not_assessed_items: notAssessed,
      completed: notAssessed === 0
    };
    
    // Recalculate overall
    let totalItems = 0, totalCompliant = 0, totalNonCompliant = 0, totalAssessed = 0;
    newCategories.forEach(cat => {
      cat.items.forEach(i => {
        totalItems++;
        if (i.status === 'compliant') { totalCompliant++; totalAssessed++; }
        else if (['non_compliant', 'partial'].includes(i.status)) { totalNonCompliant++; totalAssessed++; }
        else if (i.status !== 'not_applicable' && i.status !== 'not_assessed') { totalAssessed++; }
      });
    });
    const overallScore = totalAssessed > 0 ? (totalCompliant / totalAssessed * 100) : 0;
    
    setLocalAudit(prev => ({
      ...prev,
      categories: newCategories,
      overall_score: Math.round(overallScore * 10) / 10,
      compliant_items: totalCompliant,
      non_compliant_items: totalNonCompliant,
      total_items: totalItems
    }));

    // Save to backend
    try {
      await axios.put(`${API_URL}/api/comprehensive-audits/${localAudit.id}/item`, {
        category: newCategories[categoryIndex].category,
        item_id: item.id,
        status: item.status,
        notes: item.notes,
        corrective_action: item.corrective_action,
        priority: item.priority
      }, { headers: getAuthHeaders() });
    } catch (error) {
      console.error('Failed to save item:', error);
    }
  };

  const handleCompleteAudit = async () => {
    try {
      setSaving(true);
      await axios.post(`${API_URL}/api/comprehensive-audits/${localAudit.id}/complete`, {}, {
        headers: getAuthHeaders()
      });
      setLocalAudit(prev => ({ ...prev, status: 'completed' }));
      toast({ title: "Audit Completed", description: "Your audit has been finalized" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to complete audit", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const response = await axios.get(`${API_URL}/api/comprehensive-audits/${localAudit.id}/export/csv`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit_${localAudit.id}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast({ title: "Export Complete", description: "CSV file downloaded" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to export", variant: "destructive" });
    } finally {
      setExporting(false);
    }
  };

  const allItemsAssessed = categories.every(cat => 
    cat.items.every(item => item.status !== 'not_assessed')
  );

  return (
    <div className="space-y-6">
      {/* Audit Header */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <Button variant="ghost" onClick={onBack} className="text-white hover:bg-white/20 -ml-2 mb-2">
                <ChevronLeft className="w-4 h-4 mr-1" /> Back to Audits
              </Button>
              <h1 className="text-xl md:text-2xl font-bold">{localAudit.name}</h1>
              <p className="text-blue-100">{localAudit.company_name} • {localAudit.site_location || 'No location'}</p>
            </div>
            
            <div className="flex flex-wrap items-center gap-3">
              <Badge className={`${statusColors[localAudit.status]} border`}>
                {localAudit.status?.replace('_', ' ')}
              </Badge>
              
              <div className="text-center bg-white/10 rounded-lg px-4 py-2">
                <p className="text-2xl font-bold">{localAudit.overall_score || 0}%</p>
                <p className="text-xs text-blue-200">Overall Score</p>
              </div>

              {localAudit.status === 'draft' && (
                <Button onClick={handleStartAudit} className="bg-white text-blue-600 hover:bg-blue-50">
                  <Play className="w-4 h-4 mr-2" /> Start Audit
                </Button>
              )}
              
              {localAudit.status === 'in_progress' && allItemsAssessed && (
                <Button onClick={handleCompleteAudit} disabled={saving} className="bg-green-500 hover:bg-green-600">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Complete Audit
                </Button>
              )}
              
              {localAudit.status === 'completed' && (
                <Button onClick={handleExportCSV} disabled={exporting} variant="secondary">
                  {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
                  Export CSV
                </Button>
              )}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span>Audit Progress</span>
              <span>{localAudit.compliant_items + localAudit.non_compliant_items}/{localAudit.total_items} items</span>
            </div>
            <Progress 
              value={((localAudit.compliant_items + localAudit.non_compliant_items) / (localAudit.total_items || 1)) * 100}
              className="h-3 bg-white/20"
            />
          </div>
        </CardContent>
      </Card>

      {/* View Style Toggle */}
      <div className="flex justify-center gap-2">
        <Button 
          variant={viewStyle === 'wizard' ? 'default' : 'outline'}
          onClick={() => setViewStyle('wizard')}
          size="sm"
        >
          <ArrowRight className="w-4 h-4 mr-2" /> Step-by-Step Wizard
        </Button>
        <Button 
          variant={viewStyle === 'tabbed' ? 'default' : 'outline'}
          onClick={() => setViewStyle('tabbed')}
          size="sm"
        >
          <Grid3X3 className="w-4 h-4 mr-2" /> Tabbed View
        </Button>
      </div>

      {/* Wizard View */}
      {viewStyle === 'wizard' && (
        <div className="space-y-4">
          {/* Category Progress Indicators */}
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map((cat, idx) => {
              const Icon = getCategoryIcon(cat.category);
              const isComplete = cat.not_assessed_items === 0;
              const isCurrent = idx === currentCategoryIndex;
              return (
                <button
                  key={cat.category}
                  onClick={() => setCurrentCategoryIndex(idx)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-all ${
                    isCurrent ? 'bg-blue-600 text-white' :
                    isComplete ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span className="hidden md:inline">{cat.name.split(' ')[0]}</span>
                  {isComplete && <CheckCircle className="w-4 h-4" />}
                </button>
              );
            })}
          </div>

          {/* Current Category */}
          {currentCategory && (
            <CategoryChecklist
              category={currentCategory}
              categoryIndex={currentCategoryIndex}
              onItemUpdate={handleItemUpdate}
              disabled={localAudit.status === 'draft' || localAudit.status === 'completed'}
            />
          )}

          {/* Navigation */}
          <div className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => setCurrentCategoryIndex(prev => Math.max(0, prev - 1))}
              disabled={currentCategoryIndex === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-2" /> Previous
            </Button>
            <Button
              onClick={() => setCurrentCategoryIndex(prev => Math.min(categories.length - 1, prev + 1))}
              disabled={currentCategoryIndex === categories.length - 1}
            >
              Next <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      )}

      {/* Tabbed View */}
      {viewStyle === 'tabbed' && (
        <Tabs defaultValue={categories[0]?.category} className="space-y-4">
          <TabsList className="flex flex-wrap gap-1 h-auto p-1 bg-slate-100 rounded-xl">
            {categories.map((cat) => {
              const Icon = getCategoryIcon(cat.category);
              const isComplete = cat.not_assessed_items === 0;
              return (
                <TabsTrigger 
                  key={cat.category} 
                  value={cat.category}
                  className="data-[state=active]:bg-white rounded-lg"
                >
                  <Icon className="w-4 h-4 mr-1" />
                  <span className="hidden lg:inline">{cat.name.split(' ')[0]}</span>
                  {isComplete && <CheckCircle className="w-3 h-3 ml-1 text-green-600" />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {categories.map((cat, idx) => (
            <TabsContent key={cat.category} value={cat.category}>
              <CategoryChecklist
                category={cat}
                categoryIndex={idx}
                onItemUpdate={handleItemUpdate}
                disabled={localAudit.status === 'draft' || localAudit.status === 'completed'}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}
    </div>
  );
};

// Category Checklist Component
const CategoryChecklist = ({ category, categoryIndex, onItemUpdate, disabled }) => {
  // Get icon component outside of JSX to avoid lint warning
  const IconComponent = getCategoryIcon(category.category);
  const scoreColor = category.score >= 80 ? 'text-green-600' : 
                    category.score >= 60 ? 'text-yellow-600' : 'text-red-600';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <IconComponent className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle>{category.name}</CardTitle>
              <CardDescription>{category.description}</CardDescription>
            </div>
          </div>
          <div className="text-right">
            <p className={`text-2xl font-bold ${scoreColor}`}>{category.score}%</p>
            <p className="text-xs text-slate-500">
              {category.compliant_items}/{category.items.length - category.not_assessed_items} compliant
            </p>
          </div>
        </div>
        <Progress value={category.score} className="mt-4 h-2" />
      </CardHeader>
      <CardContent className="space-y-3">
        {category.items.map((item, itemIndex) => (
          <ChecklistItemCard
            key={item.id}
            item={item}
            itemIndex={itemIndex}
            categoryIndex={categoryIndex}
            onUpdate={onItemUpdate}
            disabled={disabled}
          />
        ))}
      </CardContent>
    </Card>
  );
};

// Checklist Item Card
const ChecklistItemCard = ({ item, itemIndex, categoryIndex, onUpdate, disabled }) => {
  const [expanded, setExpanded] = useState(false);
  const [notes, setNotes] = useState(item.notes || '');
  const [correctiveAction, setCorrectiveAction] = useState(item.corrective_action || '');

  const handleStatusChange = (status) => {
    onUpdate(categoryIndex, itemIndex, { status });
  };

  const handleSaveNotes = () => {
    onUpdate(categoryIndex, itemIndex, { notes, corrective_action: correctiveAction });
    setExpanded(false);
  };

  const statusButtons = [
    { value: 'compliant', label: 'Pass', icon: CheckCircle, color: 'bg-green-100 text-green-700 hover:bg-green-200' },
    { value: 'non_compliant', label: 'Fail', icon: XCircle, color: 'bg-red-100 text-red-700 hover:bg-red-200' },
    { value: 'partial', label: 'Partial', icon: AlertCircle, color: 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' },
    { value: 'not_applicable', label: 'N/A', icon: null, color: 'bg-slate-100 text-slate-600 hover:bg-slate-200' },
  ];

  return (
    <div className={`p-4 rounded-lg border-2 transition-all ${
      item.status === 'compliant' ? 'border-green-200 bg-green-50/50' :
      item.status === 'non_compliant' ? 'border-red-200 bg-red-50/50' :
      item.status === 'partial' ? 'border-yellow-200 bg-yellow-50/50' :
      item.status === 'not_applicable' ? 'border-slate-200 bg-slate-50/50' :
      'border-slate-200'
    }`}>
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex-1">
          <p className="font-medium text-slate-900">{item.question}</p>
          {item.osha_reference && (
            <Badge variant="outline" className="mt-1 text-xs">
              OSHA {item.osha_reference}
            </Badge>
          )}
        </div>
        
        <div className="flex flex-wrap gap-2">
          {statusButtons.map((btn) => {
            const BtnIcon = btn.icon;
            const isActive = item.status === btn.value;
            return (
              <button
                key={btn.value}
                onClick={() => !disabled && handleStatusChange(btn.value)}
                disabled={disabled}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1 transition-all ${
                  isActive ? btn.color + ' ring-2 ring-offset-1' : 'bg-white border hover:' + btn.color
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {BtnIcon && <BtnIcon className="w-4 h-4" />}
                {btn.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Notes Toggle */}
      <div className="mt-3">
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-sm text-blue-600 hover:underline"
        >
          {expanded ? 'Hide Details' : 'Add Notes / Corrective Action'}
        </button>
      </div>

      {expanded && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div>
            <Label className="text-sm">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add observation notes..."
              rows={2}
              className="mt-1"
            />
          </div>
          {(item.status === 'non_compliant' || item.status === 'partial') && (
            <div>
              <Label className="text-sm">Corrective Action Required</Label>
              <Textarea
                value={correctiveAction}
                onChange={(e) => setCorrectiveAction(e.target.value)}
                placeholder="Describe corrective action needed..."
                rows={2}
                className="mt-1"
              />
            </div>
          )}
          <Button size="sm" onClick={handleSaveNotes}>
            Save Notes
          </Button>
        </div>
      )}
    </div>
  );
};

export default AuditsPage;
